import { Constants } from '@constants';
import { Text, Tooltip } from '@iwoca/orion';
import { addDays, format } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';

import { useGetRecentlyReplacedCard } from './useGetReplacementCards';
import { CARD_DELIVERY_TIME_DAYS } from '../../../constants';
import CreditCardPaperPlane from '../../../images/credit-card-paper-plane.png';

export const ReplacementMessage = () => {
    const recentlyReplacedCard = useGetRecentlyReplacedCard();
    const intl = useIntl();

    if (!recentlyReplacedCard) return null;

    const isLostOrStolen = recentlyReplacedCard.reason === 'lost_or_stolen';
    const orderedDate = recentlyReplacedCard.orderedDate;
    const deliveryDate = addDays(
        recentlyReplacedCard.orderedDate,
        CARD_DELIVERY_TIME_DAYS
    );

    const title = isLostOrStolen ? (
        <FormattedMessage
            id="_.credit_card.dashboard.new_card.lost_or_stolen.title"
            defaultMessage="Your new card and PIN are on their way."
        />
    ) : (
        <FormattedMessage
            id="_.credit_card.dashboard.new_card.replacement.title"
            defaultMessage="Your replacement card is on its way."
        />
    );

    const description = isLostOrStolen ? (
        <FormattedMessage
            id="_.credit_card.dashboard.new_card.lost_or_stolen.description"
            defaultMessage="They will be arriving in separate packages. You will need to activate your new physical card in order to use it. To activate it, insert your card into the merchant's card reader and enter your PIN when you make your first purchase."
        />
    ) : (
        <FormattedMessage
            id="_.credit_card.dashboard.new_card.replacement.description"
            defaultMessage="To activate your replacement physical card, insert your card into the merchant's card reader and enter your PIN when you make your first purchase."
        />
    );

    return (
        <div className="mb-xl flex w-fit flex-row rounded-l border border-secondary-90 bg-primary-97 p-l max-sm:flex-col max-sm:items-center max-sm:text-center">
            <img
                src={CreditCardPaperPlane}
                // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                alt="Credit Card Paper Plane"
                className="mr-xl h-[72px] w-[72px] max-sm:mr-0"
            />
            <div className="flex flex-col max-sm:items-center max-sm:text-center">
                <Text className="text-xl font-bold max-sm:mb-m">{title}</Text>
                <Text className="text-l max-sm:mb-m max-sm:text-m">
                    {description}
                </Text>
                <div className="flex max-sm:flex-col max-sm:items-center max-sm:text-center min-m:my-m">
                    <Text className="font-medium text-s max-sm:mb-m">
                        <FormattedMessage
                            id="_.credit_card.dashboard.new_card.ordered_date"
                            defaultMessage="Ordered: {date}"
                            values={{ date: format(orderedDate, 'do MMM') }}
                        />
                    </Text>
                    <Text className="font-medium mx-l text-s max-sm:hidden">
                        <FormattedMessage
                            id="_.credit_card.dashboard.new_card.separator"
                            defaultMessage="|"
                        />
                    </Text>
                    <Text className="font-medium text-s max-sm:mb-m">
                        <FormattedMessage
                            id="_.credit_card.dashboard.new_card.expected_delivery_date"
                            defaultMessage="Expected delivery by: {date}"
                            values={{ date: format(deliveryDate, 'do MMM') }}
                        />
                    </Text>
                </div>
                <Tooltip
                    variant="secondary"
                    content={intl.formatMessage(
                        {
                            id: '_.credit_card.dashboard.new_card.report_problem.tooltip',
                            defaultMessage: 'Please call us on {phone}'
                        },
                        { phone: Constants.CREDIT_CARD_PHONE_NUMBER.display }
                    )}
                >
                    <span className="font-medium inline-block cursor-pointer text-s text-primary-40 underline">
                        <FormattedMessage
                            id="_.credit_card.dashboard.new_card.report_problem"
                            defaultMessage="Report a problem"
                        />
                    </span>
                </Tooltip>
            </div>
        </div>
    );
};
